import EventEmitter from "events";
import { AnimationAction, AnimationClip, AnimationMixer, Bone, Material, Mesh, MeshStandardMaterial, Object3D, Scene, Skeleton, SkinnedMesh, Texture } from "three";
import { GUI } from "lil-gui";
import { ScenarioOption, SelectionMode } from "../animation/type";
import AnimationScenario from "../animation/animation-scenario";
import { createVRMAnimationClip } from "@pixiv/three-vrm-animation";
import Loader from "../../../base/loader/loader";
import type ThreeJSApp from "../../../app";
import LipSync from "../lipsync/lipsync";
import { selectConsistentAnimationSet, selectIndependentAnimationVariants, extractAnimationsFromScenarioConfigs } from "./util";
import { AvatarConfig } from "./type";
import { updateVrmFrame } from "./update-vrm-frame";
import { collectMeshTextures, improveAvatarTextureSampling } from "./texture-sampling";

const BLOOM_SCENE = 1;

export default class BaseAvatarPlayer extends EventEmitter {
  protected debugFolder: GUI | null = null;
  protected config: AvatarConfig;

  protected root: ThreeJSApp;
  protected loader: Loader;
  protected scene: Scene;
  protected scenario!: AnimationScenario<string>;
  protected lipsync!: LipSync;

  protected mixer!: AnimationMixer;
  protected model!: Object3D;
  protected animations!: { [name: string]: AnimationAction };
  // .vrma(VRM 정규화 본) 애니메이션을 쓰는 아바타의 VRM 인스턴스.
  // 매 프레임 humanoid와 선택적 Spring Bone을 갱신해 실제 본에 움직임을 전파한다.
  // (GLB 아바타는 실제 본을 직접 구동하므로 이 값이 undefined → 아무 영향 없음)
  protected animationVrm?: Parameters<typeof createVRMAnimationClip>[1];

  protected bloomMeshes: Mesh[] = [];
  protected bloomMeshMaterials: MeshStandardMaterial[] = [];

  protected currScenarioName!: string;
  protected isReady: boolean;
  protected fps: number;
  protected delta: number;

  constructor(root: ThreeJSApp, config: AvatarConfig) {
    super();

    this.root = root;
    this.config = config;
    this.loader = this.root.loader;
    this.scene = this.root.scene;

    this.isReady = false;
    this.fps = 30;
    this.delta = 1 / this.fps;

    this.load();
  }

  async load() {

    // 로더의 진행률 이벤트를 리스닝하여 상위로 전달
    this.loader.on('totalProgress', (progressData) => {
      this.emit('loadProgress', progressData);
    });

    await this.loader.loadAll(this.config.resources);

    await this.setAvatar();

    this.setLipSyncModule();

    await this.setAnimations();

    setTimeout(() => {
      if (this.bloomMeshes.length > 0) {
        this.setupBloomEffect();
      }
      this.root.markNeedsUpdate();
    }, 100);

    this.isReady = true;
    this.emit("ready");
  }

  protected async setAvatar() {
    const modelResource = this.loader.resources[this.config.modelKey];
    if (!modelResource) return;

    const scene = modelResource.file.scene;
    if (!scene) return;

    this.model = scene;

    // 아바타별 스케일 설정
    if (this.config.scale) {
      const { x, y, z } = this.config.scale;
      this.model.scale.set(x, y, z);
    }

    if (this.config.rotation) {
      const { x, y, z } = this.config.rotation;
      this.model.rotation.set(x, y, z);
    }

    if (this.config.position) {
      const { x, y, z } = this.config.position;
      this.model.position.set(x, y, z);
    }

    this.model.traverse((child) => {
      this.setupChildMesh(child);
    });

    // VRM 저작 샘플러가 LINEAR_MIPMAP_NEAREST·anisotropy 1이라 경사면이 뭉개진다.
    // 트라이리니어 + anisotropy 4로 축소 샘플링만 올린다 (전송량·GPU 메모리 불변).
    improveAvatarTextureSampling(
      collectMeshTextures(this.model),
      this.root.renderer.instance.capabilities.getMaxAnisotropy(),
    );

    this.scene.add(this.model);
  }

  protected setupChildMesh(child: Object3D) {
    if (child.name.includes('Prop_Effect')) {
      child.visible = false;
      return;
    }

    if (child instanceof Mesh && child.material) {
      this.applyBloomEffect(child);
    }

    if (child?.frustumCulled) {
      child.frustumCulled = false;
    }

    if ((child as SkinnedMesh)?.isSkinnedMesh) {
      this.setupSkinnedMesh(child as SkinnedMesh);
    }
  }

  protected applyBloomEffect(mesh: Mesh) {
    const material = mesh.material as MeshStandardMaterial;

    // 아바타별 블룸 설정 적용
    const bloomConfig = this.config.bloom;
    if (bloomConfig && this.shouldApplyBloom(mesh, bloomConfig)) {
      material.emissiveIntensity = bloomConfig.emissiveIntensity;

      // 아바타별 특수 설정
      if (bloomConfig.specialSettings) {
        this.applySpecialSettings(mesh, material);
      }

      mesh.layers.disableAll();
      mesh.layers.enable(0);
      mesh.layers.enable(BLOOM_SCENE);
      this.bloomMeshes.push(mesh);
    } else {
      material.emissiveIntensity = 0;
    }
  }

  protected shouldApplyBloom(mesh: Mesh, bloomConfig: AvatarConfig['bloom']): boolean {
    if (!bloomConfig) return false;

    return bloomConfig.meshNames.some((name: string) => mesh.name.includes(name));
  }

  protected applySpecialSettings(mesh: Mesh, material: MeshStandardMaterial) {
    // 상속 클래스에서 오버라이드 가능
    if (this.config.type === 'glyx') {
      this.applyGlyxSpecialSettings(mesh, material);
    }
  }

  protected applyGlyxSpecialSettings(mesh: Mesh, material: MeshStandardMaterial) {
    if (mesh.name === "Head_Eyes") {
      material.emissiveMap = null;
      material.emissive.setRGB(20 / 255, 50 / 255, 255 / 255);
    }

    // Specular와 Irradiance 효과
    if (material.roughness) {
      material.roughness *= 0.85;
    }
    if ('transmission' in material) {
      (material as Record<string, unknown>).transmission = 1;
    }
    material.needsUpdate = true;
  }

  protected setupSkinnedMesh(mesh: SkinnedMesh) {
    if (mesh.name === "Body_Armholehip") {
      mesh.receiveShadow = true;
    } else if (!mesh.name.includes("Prop_Effect")) {
      mesh.castShadow = true;
    }
  }

  protected setupBloomEffect() {
    if (!this.root.renderer) {
      console.warn("⚠️ renderer가 초기화되지 않았습니다.");
      return;
    }
  }

  protected setLipSyncModule() {
    if (!this.model) return;

    // 🔍 디버깅: morphTarget 정보 출력
    console.log(`🎭 [LipSync] Avatar Type: ${this.config.type}`);
    this.model.traverse((child) => {
      const mesh = child as Mesh;
      if (mesh.morphTargetInfluences && mesh.morphTargetDictionary) {
        console.log(`🎭 [LipSync] Mesh: ${mesh.name}`);
        console.log(`🎭 [LipSync] morphTargetInfluences count: ${mesh.morphTargetInfluences.length}`);
        console.log(`🎭 [LipSync] morphTargetDictionary:`, mesh.morphTargetDictionary);
      }
    });

    this.mixer = new AnimationMixer(this.model);
    this.lipsync = new LipSync(this.root, this.model, this.mixer, this.fps, this.delta, this.config);
  }

  protected async setAnimations() {
    // 애니메이션 리소스만 필터링
    const animationResources = Object.entries(this.loader.resources)
      .filter(([, resource]) => resource.type === "animation_only")
      .map(([name, resource]) => ({
        name,
        ...resource,
      }));


    // 모델의 morphTarget 정보 수집
    let modelMorphTargetCount = 0;
    if (this.model) {
      this.model.traverse((child) => {
        if ((child as Mesh).morphTargetInfluences) {
          const morphTargetInfluences = (child as Mesh).morphTargetInfluences;
          if (morphTargetInfluences && morphTargetInfluences.length > modelMorphTargetCount) {
            modelMorphTargetCount = morphTargetInfluences.length;
          }
        }
      });
    }


    // .vrma 리소스는 로드 시 animations가 비어있고 VRMAnimation 객체만 userData에 담겨온다.
    // 현재 모델 VRM에 맞춰 createVRMAnimationClip으로 실제 AnimationClip으로 변환(리타게팅)한다.
    // (GLB/FBX 아바타는 file.animations가 이미 채워져 있어 이 분기를 타지 않음 → 영향 없음)
    const modelFile = this.loader.resources[this.config.modelKey]?.file;
    const targetVrm = (modelFile?.userData as { vrm?: unknown } | undefined)?.vrm as
      | Parameters<typeof createVRMAnimationClip>[1]
      | undefined;

    this.animations = Object.fromEntries(
      animationResources.map(({ name, file }) => {
        let animations = file.animations;
        const vrmAnimations = (file.userData as { vrmAnimations?: unknown[] } | undefined)?.vrmAnimations;
        if ((!animations || animations.length === 0) && vrmAnimations?.length && targetVrm) {
          animations = vrmAnimations
            .map((va) => createVRMAnimationClip(va as Parameters<typeof createVRMAnimationClip>[0], targetVrm))
            .filter((clip): clip is AnimationClip => !!clip && clip.tracks.length > 0);
          // 이 아바타는 정규화 본 기반 애니메이션 → update()에서 VRM 프레임 전파 필요
          this.animationVrm = targetVrm;
        }
        if (!animations || animations.length === 0) {
          throw new Error(`No animations found for resource: ${name}`);
        }


        const animationsFiltered = animations.map((animation) => {
          // Props 트랙은 제거하되, morphTarget 트랙은 조절
          const filteredTracks = animation.tracks
            .filter((track) => !track.name.includes('Props'))
            .filter((track) => {
              // speaking이 들어간 애니메이션에서는 morphTarget 트랙 완전히 제거
              if (animation.name.includes("speaking") && track.name.includes('morphTarget')) {
                return false;
              }
              return true;
            });

          const adjustedTracks = filteredTracks.map((track) => {
            if (track.name.includes('morphTarget')) {
              // morphTarget 트랙인 경우, 모델의 morphTarget 수에 맞게 조절
              const clonedTrack = track.clone();

              // 현재 트랙의 값 배열과 시간 배열 길이 확인
              const currentValues = clonedTrack.values;
              const timeCount = clonedTrack.times.length;

              if (modelMorphTargetCount > 0 && timeCount > 0) {
                // 각 시간 프레임마다 modelMorphTargetCount만큼의 morphTarget 값이 필요
                const newValues = new Float32Array(timeCount * modelMorphTargetCount);

                // 각 시간 프레임별로 처리
                for (let timeIndex = 0; timeIndex < timeCount; timeIndex++) {
                  // 0~51번까지는 기존 값 사용 (있는 경우)
                  for (let morphIndex = 0; morphIndex < Math.min(52, modelMorphTargetCount); morphIndex++) {
                    const sourceIndex = timeIndex * (currentValues.length / timeCount) + morphIndex;
                    if (sourceIndex < currentValues.length) {
                      newValues[timeIndex * modelMorphTargetCount + morphIndex] = currentValues[sourceIndex];
                    } else {
                      newValues[timeIndex * modelMorphTargetCount + morphIndex] = 0;
                    }
                  }

                  // 52번부터 모델의 morphTarget 수까지 0으로 채움
                  for (let morphIndex = 52; morphIndex < modelMorphTargetCount; morphIndex++) {
                    newValues[timeIndex * modelMorphTargetCount + morphIndex] = 0;
                  }
                }

                clonedTrack.values = newValues;
              }

              return clonedTrack;
            }
            return track;
          });

          return { ...animation, tracks: adjustedTracks };
        }) || [];

        const animation = animationsFiltered[0];
        animation.name = name;

        const action = this.mixer.clipAction(animation as AnimationClip);
        return [name, action];
      })
    );

    this.scenario = new AnimationScenario(this.animations);
    // scenarioConfigs에서 애니메이션 배열만 추출해서 설정
    this.scenario.scenarios = extractAnimationsFromScenarioConfigs(this.config.scenarioConfigs);

    if (this.root.debug?.active) {
      this.setupDebugGUI();
    }
  }

  protected setupDebugGUI() {
    if (!this.root.debug?.ui) return;

    this.debugFolder = this.root.debug.ui.addFolder(`Avatar Animations (${this.config.type})`);
    this.debugFolder.close();

    // 기존 시나리오들을 자동으로 디버그 메뉴에 추가
    if (this.config.scenarioConfigs) {
      for (const scenarioName of Object.keys(this.config.scenarioConfigs)) {
        this.debugFolder.add({
          [scenarioName]: () => {
            console.log(`🎭 Playing ${scenarioName} scenario`);
            this.playScenario(scenarioName);
          }
        }, scenarioName);
      }
    }
  }

  protected modifyScenario(name: string, type: "random_behavior" | "random_type") {
    let newScenario;

    // scenarioConfigs에서 해당 시나리오의 애니메이션 배열 가져오기
    const scenarioConfig = this.config.scenarioConfigs[name];
    if (!scenarioConfig) return;

    const animations = scenarioConfig.animations;

    // 애니메이션 개수에서 직접 계산 (중복 제거!)
    const animationCount = animations.length;

    // 기존 방식: 각각 독립적인 변형 선택 (hi01, start02 등)
    if (type === "random_behavior") {
      newScenario = selectIndependentAnimationVariants(animations, 1, animationCount);
    }

    // 기존 방식: 일관된 세트 선택 (listening01_a, listening01_b)
    if (type === "random_type") {
      newScenario = selectConsistentAnimationSet(animations, animationCount);
    }

    if (newScenario) {
      this.scenario.modifyScenario(name, newScenario);
    }
  }

  protected processSelectionMode(animations: string[], selectionMode: SelectionMode): string[] {
    // 애니메이션 배열에서 직접 개수 계산 (더 간단!)
    const count = animations.length;

    switch (selectionMode) {
      case 'consistent_set': {
        // 일관된 세트 선택: 모든 애니메이션이 같은 번호 변형을 사용
        // 다변형 목록(예: listening01_a/01_b/02_a/02_b)은 치환 후 같은 이름이 중복되므로 순서 보존 제거
        const selected = selectConsistentAnimationSet(animations, count);
        return Array.from(new Set(selected));
      }
      case 'independent_variants':
        // 독립적인 변형 선택: 각 애니메이션마다 서로 다른 번호 변형 사용 가능
        return selectIndependentAnimationVariants(animations, 1, count);
      case 'none':
      default:
        return animations;
    }
  }

  // 새로운 시나리오 재생 메서드
  public playScenario(name: string) {
    const scenarioConfig = this.config.scenarioConfigs[name];
    if (!scenarioConfig) {
      console.warn(`시나리오 '${name}'를 찾을 수 없습니다.`);
      return;
    }

    // 선택 모드에 따라 애니메이션 처리
    let processedAnimations = scenarioConfig.animations;
    if (scenarioConfig.selectionMode && scenarioConfig.selectionMode !== 'none') {
      processedAnimations = this.processSelectionMode(
        scenarioConfig.animations,
        scenarioConfig.selectionMode
      );
    }

    // 'once' 다중 클립: 설정 로드 시점이 아니라 재생 시점에 1개를 랜덤 선택 (매 재생 다른 클립)
    if (scenarioConfig.playMode === 'once' && processedAnimations.length > 1) {
      processedAnimations = [processedAnimations[Math.floor(Math.random() * processedAnimations.length)]];
    }

    console.log(`🎭 [Scenario] ${name}: ${processedAnimations.join(', ')}`);

    // 시나리오 수정
    this.scenario.modifyScenario(name, processedAnimations);

    // playMode에 따른 옵션 설정
    const options: Partial<ScenarioOption> = {
      duration: scenarioConfig.blendDuration || 0.2,
      isLastAnimationLoop: false,
      isRandom: false,
      blendDelta: 10,
      ...scenarioConfig.options
    };

    // playMode별 특별 처리
    switch (scenarioConfig.playMode) {
      case 'once':
        options.isLastAnimationLoop = false;
        break;
      case 'last_loop':
        options.isLastAnimationLoop = true;
        break;
      case 'random_loop':
        options.isRandom = true;
        break;
      case 'sequential_loop':
        options.isRandom = false;
        break;
    }

    this.currScenarioName = name;

    // nextScenario가 있으면 시나리오 종료 시 자동으로 다음 시나리오 실행
    if (scenarioConfig.nextScenario) {
      this.scenario.onEnd = () => {
        this.playScenario(scenarioConfig.nextScenario!);
      };
    } else {
      // nextScenario가 없으면 onEnd 콜백을 완전히 초기화 (이전 시나리오의 콜백이 남아있지 않도록)
      this.scenario.onEnd = null;
    }

    this.scenario?.playScenarioByName(name, options);
    this.root.markNeedsUpdate();
  }

  public onEndScenario(callback: (...args: unknown[]) => void) {
    this.scenario.onEnd = callback;
    return this;
  }

  public play(name: string, type?: "random_behavior" | "random_type" | "", options?: Partial<ScenarioOption>) {
    if (this.currScenarioName && this.currScenarioName === name) return;

    if (type) {
      this.modifyScenario(name, type);
    }

    this.currScenarioName = name;
    this.scenario?.playScenarioByName(name, options);
    this.root.markNeedsUpdate();
  }

  public stop() {
    this.scenario?.stopScenario();
    this.root.markNeedsUpdate();
  }

  public playLipSync(blendshapeList: number[][], duration: number = 0.2) {
    this.lipsync.play(blendshapeList, { duration });
    this.root.markNeedsUpdate();
  }

  public stopLipSync() {
    this.lipsync.stop();
    this.root.markNeedsUpdate();
  }

  public destroy() {
    try {
      this.bloomMeshes.forEach((mesh, index) => {
        mesh.layers.disable(BLOOM_SCENE);
        if (index < this.bloomMeshMaterials.length) {
          mesh.material = this.bloomMeshMaterials[index];
        }
      });
      this.bloomMeshes = [];
      this.bloomMeshMaterials = [];
    } catch (error) {
      console.error("❌ 블룸 효과 정리 중 오류:", error);
    }

    if (this.mixer) this.mixer.stopAllAction();
    if (this.model && this.mixer) this.mixer.uncacheRoot(this.model);

    if (this.animations) {
      Object.values(this.animations).forEach((action) => {
        if (this.mixer) {
          this.mixer.uncacheAction(action.getClip());
          this.mixer.uncacheClip(action.getClip());
        }
      });
    }

    this.model?.traverse((child) => {
      if (child instanceof Bone) {
        child.clear();
      }

      if (child instanceof Mesh) {
        child.geometry?.dispose();

        const materials = Array.isArray(child.material) ? child.material : [child.material];
        materials.forEach((material) => {
          if (material instanceof Material) {
            // 타입 안전성을 위해 명시적으로 텍스처 맵들을 정리
            const materialRecord = material as unknown as Record<string, unknown>;
            ['map', 'lightMap', 'bumpMap', 'normalMap', 'specularMap', 'envMap', 'emissiveMap', 'roughnessMap', 'metalnessMap'].forEach(key => {
              if (materialRecord[key] instanceof Texture) {
                (materialRecord[key] as Texture).dispose();
              }
            });
            material.dispose();
          }
        });
      }
    });

    if ("skeleton" in this.model) {
      const skeleton = this.model["skeleton"] as Skeleton;
      skeleton?.boneTexture?.dispose();
      skeleton?.bones?.forEach((bone: Bone) => bone.clear());
      this.model.clear();
    }

    if (this.debugFolder) {
      this.debugFolder.destroy();
    }

    this.removeAllListeners();
  }

  public update(delta: number) {
    if (!this.mixer || !this.model) return;

    this.mixer.update(delta);

    // VRM(.vrma) 아바타: 정규화 본과 opt-in Spring Bone을 전파한다.
    // 표정(morph)은 갱신하지 않아 커스텀 expression/lipsync 파이프라인은 그대로 유지된다.
    updateVrmFrame(this.animationVrm, delta, this.config.springBoneEnabled ?? false);

    if (this.scenario) {
      this.scenario?.update();
    }

    this.root.markNeedsUpdate();
  }

  public get ready() {
    return this.isReady;
  }

  public get avatarType() {
    return this.config.type;
  }

  // 아바타 모델 객체 반환 (raycast용)
  public getModel(): Object3D | null {
    return this.model || null;
  }
}
